<script setup lang="ts">
import type { MenuListItem } from '@/constants/menuLists';

import { reactive, watch } from 'vue';
import { useRoute } from 'vue-router';

import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import { useAuthStore } from '@/stores/useAuthStore';
import { useSidebarsStore } from '@/stores/useSidebarsStore';
import {
  mainList,
  quoteTitle,
  reportsTitle,
  smsTitle,
} from '@/constants/menuLists';
import * as routeNames from '@/constants/routeNames';
import { brandConfig } from '@/brand';
import { filterMenuByBrand } from '@/brand/menuFilter';

export interface Props {
  isMini?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  isMini: false,
});

// TODO
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
const route = useRoute() || {}; // TODO mock useRoute
const sidebarsStore = useSidebarsStore();
const authStore = useAuthStore();

const filteredMainList = filterMenuByBrand(mainList, brandConfig.brand);
const drawerSelected: string[] = reactive([]);

watch(
  () => route.name,
  () => {
    if (typeof route.name === 'string') {
      drawerSelected[0] = route.name;
    }
  },
  { immediate: true },
);

watch(
  [() => sidebarsStore.sidebars.quote, () => sidebarsStore.sidebars.reports],
  () => {
    if (!sidebarsStore.sidebars.quote && !sidebarsStore.sidebars.reports) {
      if (typeof route.name === 'string') {
        drawerSelected[0] = route.name;
      }
    }
  },
);

function toggleDrawerByItem(item: MenuListItem): void {
  if (item.title === quoteTitle) {
    sidebarsStore.sidebars.quote = !sidebarsStore.sidebars.quote;
  } else if (item.title === reportsTitle) {
    sidebarsStore.sidebars.reports = !sidebarsStore.sidebars.reports;
  } else if (item.title === smsTitle) {
    sidebarsStore.sidebars.sms = !sidebarsStore.sidebars.sms;
  }
}

function handleItemClick(item: MenuListItem): void {
  drawerSelected[0] = `title: ${item.title}`;
  toggleDrawerByItem(item);
}

function isSubMenuActive(item: MenuListItem): boolean {
  if (drawerSelected[0] === `title: ${item.title}`) return true;
  if (item.subList?.map((item) => item.to?.name).includes(drawerSelected[0])) {
    return true;
  }
  return false;
}
</script>

<template>
  <v-list
    :class="[$style.mainSideBarMenuVList, { [$style.isMini]: props.isMini }]"
    data-testid="mainSideBarMenuVList"
    :selected="drawerSelected"
    density="compact"
    nav
  >
    <template v-for="item in filteredMainList" :key="item.title">
      <template v-if="!item.staff || authStore.isStaff">
        <template v-if="item.to?.name">
          <v-list-item
            :ripple="false"
            :class="[
              $style.item,
              { [$style.oldVersion]: item.title === 'Old Version' },
            ]"
            :prepend-icon="item.icon"
            :value="item.title"
            :disabled="item.disabled"
            :active="
              drawerSelected[0] === item.to.name ||
              (item.to.name === routeNames.SEARCH &&
                [
                  routeNames.SEARCH,
                  routeNames.SEARCH_JOB_ID,
                  routeNames.SEARCH_ADVANCED,
                  routeNames.SEARCH_POSTCODE,
                  routeNames.SEARCH_SUBURBS,
                ].includes(drawerSelected[0]))
            "
            :to="!item.disabled ? item.to : undefined"
          >
            <KParentTooltip v-if="props.isMini">
              {{ item.title }}
            </KParentTooltip>
            {{ item.title }}
          </v-list-item>
        </template>
        <template v-else>
          <v-list-item
            :ripple="false"
            :class="$style.item"
            :prepend-icon="item.icon"
            :value="item.title"
            :disabled="item.disabled || !item.subList"
            :active="isSubMenuActive(item)"
            @click="!item.disabled ? handleItemClick(item) : undefined"
          >
            <KParentTooltip v-if="props.isMini">
              {{ item.title }}
            </KParentTooltip>
            {{ item.title }}
          </v-list-item>
        </template>
      </template>
    </template>
  </v-list>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.mainSideBarMenuVList {
  font-size: 1rem;
  line-height: 24px;
  padding: 24px 16px !important;

  :global {
    .v-list-item {
      color: colors.$color-text-primary;
      border-radius: 4px;
      padding: 0 12px !important;
      margin: 0 0 4px 0 !important;
    }
    .v-list-item:hover {
      background-color: colors.$color-brand-100;
    }
    .v-list-item:active {
      background-color: colors.$color-brand-100;
    }
    .v-list-item--active {
      color: colors.$color-brand-800;
      background-color: colors.$color-brand-100;
    }
    .v-list-item__overlay {
      display: none;
    }
    .v-list-item__spacer {
      display: none;
    }
  }
}

  .item {
    min-height: 48px !important;

    :global {
        .v-icon {
          opacity: 1 !important;
          font-size: 20px;
        }
      .v-list-item__content {
        opacity: 1 !important;
        white-space: nowrap;
        margin-left: 8px;
      }
    }

    .isMini & {
      :global {
        .v-list-item__prepend {
          grid-area: content;
          justify-content: center;
        }
          .v-icon {
          }
        .v-list-item__content {
          display: none;
        }
      }
    }
  }

.mainSideBarMenuVList .item.oldVersion {
  margin-top: 16px !important;
  border-top: 1px solid colors.$color-brand-200;
  border-radius: 0 !important;
}
</style>
